import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser, Role } from '@touristation/shared';
import { apiGet, apiPost } from '../api/client';
import { clearToken, getRefreshToken, getToken, setSession } from './session';

interface AuthState {
  ready: boolean;
  authenticated: boolean;
  sub: string;
  name: string;
  email: string;
  roles: Role[];
  hasRole: (role: Role) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);

  // On mount, rehydrate the session from a stored token (if any). An invalid or
  // expired token yields a 401, which the API client handles by clearing it.
  useEffect(() => {
    let active = true;
    if (!getToken()) {
      setReady(true);
      return;
    }
    apiGet<AuthUser>('/auth/me')
      .then((me) => active && setUser(me))
      .catch(() => active && clearToken())
      .finally(() => active && setReady(true));
    return () => {
      active = false;
    };
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    const { accessToken, refreshToken, user: me } = await apiPost<{
      accessToken: string; refreshToken: string; user: AuthUser;
    }>('/auth/login', { email, password });
    setSession(accessToken, refreshToken);
    setUser(me);
  };

  const logout = (): void => {
    // Best-effort server-side revocation of the refresh token.
    const refreshToken = getRefreshToken();
    if (refreshToken) void apiPost('/auth/logout', { refreshToken }).catch(() => undefined);
    clearToken();
    setUser(null);
  };

  const value: AuthState = {
    ready,
    authenticated: !!user,
    sub: user?.sub ?? '',
    name: user?.name ?? '',
    email: user?.email ?? '',
    roles: user?.roles ?? [],
    hasRole: (role) => (user?.roles ?? []).includes(role),
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
